/**
 * Reorderable client-side list hook — shared by both Business Config sub-modules.
 *
 * Angular parity: the server returns the full config list once; search, pagination
 * and reorder-preview are all client-side. Drag-reorder computes absolute indices
 * from the current page offset (`pageStart`) and persists the full new order; it is
 * blocked while a search filter is active.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { DragEvent } from 'react'
import { showSuccess, showError, showApiError } from '@/lib/utils/notifications'
import { BUSINESS_CONFIG_DEFAULT_PER_PAGE } from '../constants'
import type { ReorderableListConfig, ReorderableListState } from '../types'

export function useReorderableClientList<T>(
  config: ReorderableListConfig<T>,
): ReorderableListState<T> {
  const {
    fetchAll,
    getId,
    matchesSearch,
    persistOrder,
    reorderSuccessMessage,
    searchGuardMessage,
  } = config

  const [allItems, setAllItems] = useState<T[]>([])
  const [loading, setLoading] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState<number>(BUSINESS_CONFIG_DEFAULT_PER_PAGE)

  const [draggingId, setDraggingId] = useState<number | null>(null)
  const mountedRef = useRef(false)
  const dragActiveRef = useRef(false)
  const dragLocalIndexRef = useRef<number | null>(null)
  const orderChangedRef = useRef(false)
  const originalOrderRef = useRef<T[]>([])
  const allItemsRef = useRef<T[]>(allItems)
  allItemsRef.current = allItems

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      setAllItems(await fetchAll())
    } catch (error) {
      showApiError(error)
    } finally {
      setLoading(false)
    }
  }, [fetchAll])

  useEffect(() => {
    if (mountedRef.current) return
    mountedRef.current = true
    void reload()
  }, [reload])

  const filtered = useMemo(() => {
    const query = appliedSearch.trim().toLowerCase()
    if (!query) return allItems
    return allItems.filter((item) => matchesSearch(item, query))
  }, [allItems, appliedSearch, matchesSearch])

  const totalRecords = filtered.length
  const lastPage = Math.max(1, Math.ceil(totalRecords / perPage))
  const currentPage = Math.min(Math.max(1, page), lastPage)
  const pageStart = (currentPage - 1) * perPage
  const pageItems = useMemo(
    () => filtered.slice(pageStart, pageStart + perPage),
    [filtered, pageStart, perPage],
  )
  const hasActiveSearch = appliedSearch.trim().length > 0

  const submitSearch = useCallback(() => {
    setAppliedSearch(searchInput)
    setPage(1)
  }, [searchInput])

  const resetSearch = useCallback(() => {
    setSearchInput('')
    setAppliedSearch('')
    setPage(1)
  }, [])

  const changePerPage = useCallback((n: number) => {
    setPerPage(n)
    setPage(1)
  }, [])

  const onGripDragStart = useCallback(
    (localIndex: number) => (event: DragEvent) => {
      // Angular blocks reorder while a search is active.
      if (hasActiveSearch) {
        showError(searchGuardMessage)
        event.preventDefault()
        return
      }
      dragActiveRef.current = true
      dragLocalIndexRef.current = localIndex
      orderChangedRef.current = false
      originalOrderRef.current = allItemsRef.current
      const dragged = allItemsRef.current[pageStart + localIndex]
      setDraggingId(dragged ? getId(dragged) : null)
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', String(localIndex))
      // Drag the whole row as the preview (not just the grip icon). The grip stays the
      // handle so row buttons / text remain clickable; the ghost is the full <tr>.
      const row = (event.currentTarget as HTMLElement).closest('tr')
      if (row) {
        const rect = row.getBoundingClientRect()
        event.dataTransfer.setDragImage(row, event.clientX - rect.left, event.clientY - rect.top)
      }
    },
    [hasActiveSearch, searchGuardMessage, pageStart, getId],
  )

  const onRowDragOver = useCallback((event: DragEvent) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }, [])

  /**
   * Live preview: move the dragged item into the hovered slot as you drag, so the rows
   * shift and the faded placeholder shows exactly where the row will land. Order is only
   * persisted on drag-end.
   */
  const onRowDragEnter = useCallback(
    (localIndex: number) => (event: DragEvent) => {
      event.preventDefault()
      if (!dragActiveRef.current) return
      const from = dragLocalIndexRef.current
      if (from === null || from === localIndex) return
      const source = allItemsRef.current
      const fromAbs = pageStart + from
      const toAbs = pageStart + localIndex
      if (fromAbs < 0 || fromAbs >= source.length || toAbs < 0 || toAbs >= source.length) return
      const next = [...source]
      const [moved] = next.splice(fromAbs, 1)
      next.splice(toAbs, 0, moved)
      setAllItems(next)
      dragLocalIndexRef.current = localIndex
      orderChangedRef.current = true
    },
    [pageStart],
  )

  const onRowDrop = useCallback((event: DragEvent) => {
    // The move is already applied live on dragEnter; just prevent the browser default.
    event.preventDefault()
  }, [])

  const onGripDragEnd = useCallback(
    (event: DragEvent) => {
      const wasActive = dragActiveRef.current
      const changed = orderChangedRef.current
      const cancelled = event.dataTransfer.dropEffect === 'none'
      dragActiveRef.current = false
      dragLocalIndexRef.current = null
      orderChangedRef.current = false
      setDraggingId(null)
      if (!wasActive) return
      if (cancelled) {
        // Dropped outside a valid target (or Escape) — revert the live preview.
        if (changed) setAllItems(originalOrderRef.current)
        return
      }
      if (!changed) return
      persistOrder(allItemsRef.current.map(getId))
        .then(() => showSuccess(reorderSuccessMessage))
        .catch((error) => {
          showApiError(error)
          void reload()
        })
    },
    [persistOrder, getId, reorderSuccessMessage, reload],
  )

  return {
    loading,
    searchInput,
    setSearchInput,
    submitSearch,
    resetSearch,
    page: currentPage,
    setPage,
    perPage,
    changePerPage,
    totalRecords,
    lastPage,
    pageStart,
    pageItems,
    hasActiveSearch,
    reload,
    dnd: {
      draggingId,
      onGripDragStart,
      onGripDragEnd,
      onRowDragOver,
      onRowDragEnter,
      onRowDrop,
    },
  }
}
